import React, { useContext, useEffect, useMemo, useState } from "react";
import { DragOverlay, useDroppable } from '@dnd-kit/core';

import { Course, CourseValidationError } from "src/types/coaching/courses";

import { useDispatch } from "react-redux";
import { addContentToCourse, createAssessment, createCourseSection, editCourse, fetchDraftCourse, publishCourse, rearrangeEntitiesInCourse, removeEntityFromCourse, updateCourse, updateValidationErrorsForCourse, validateCourse } from "src/redux/actions/coaching/courses";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AddAssetsDialog } from "src/components/asset/add";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { animated, useSpring, useTransition } from "@react-spring/web";

import { getCoursePreferences, getIndexForContent, prepareCourseRequest, updateCoursePreferences, validateCourseRequest } from "../helper";
import { nanoid } from "nanoid";
import { CourseContext, CourseContextProps } from "../context";
import { CourseMenuBar } from "../components/menu";
import { AssessmentCard, ContentEmptyCard, ContentPlaceholderCard, DropIndicator, LessonCard, SectionCard, PodcastCard, SimulationCard } from "../components/cards";
import { EditCourseContent, SelectContentType } from "./content";

import { PublishView } from "..";
import { AddLearnersDialog } from "../components/learners/add";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import Text from "src/components/text";

import _ from "lodash";
import cx from 'classnames';
import styles from "./structure.module.scss";


export const EditCourseStructurePage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const id = params.id;

  const contentIndex = useMemo(() => {
    return parseInt(searchParams.get("index") || "0");
  }, [searchParams]);

  const sectionIndex = useMemo(() => {
    return parseInt(searchParams.get("child") || "0");
  }, [searchParams]);

  const persistedCourse: Course = useSelector((state: any) =>
    _.get(state.courses, `course[${id}].item`) as Course,
  );

  const loading = useSelector((state: any) => {
    return _.get(state.courses, `course[${id}].loading`);
  });

  const course: Course = useSelector((state: any) => {
    return _.get(state.courses, `edit.item`) as Course;
  });

  const courses = useSelector((state: any) => {
    return _.get(state, 'courses');
  });

  const activeCourseContent = useMemo(() => {
    const content = _.get(course, `contents[${contentIndex}]`);
    if (sectionIndex >= 0 && content?.entityType === "section") {
      return _.get(content, `contents[${sectionIndex}]`);
    }
    
    return content;
  }, [course, contentIndex, sectionIndex]);

  const { loadingLearners, learners, currentPage, nextPage, hasMoreItems } = useSelector((state: any) => {
    const learnersState = _.get(state, 'courses.learners');
    const loadingLearners = _.get(learnersState, 'loading');
    const pagination = _.get(learnersState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const currentPage = _.get(pagination, "currentPage", 0);

    const learners = (_.get(learnersState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loadingLearners: loadingLearners,
      learners: learners,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  const initialEntities: any[] = [];

  const [showAddAssets, setShowAddAssets] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [showAddLearner, setShowAddLearner] = useState(false);
  const [showTypeSelect, setShowTypeSelect] = useState(false);
  const [showLearningBrowser, setShowLearningBrowser] = useState(false);
  const [selected, setSelected] = useState<any>({ option: null, entities: initialEntities });
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [showAssetsDialog, setShowAssetsDialog] = useState(false);

  const [menuState, setMenuState] = useState({
    primaryAction: null,
    secondaryAction: null
  });

  const onContentTypeSelect = (parentSectionId?: string | null) => {
    setSelectedSectionId(parentSectionId ?? null);
    setShowAssetsDialog(false);
    setShowTypeSelect(true);
  };


  const [courseContext, setCourseContext] = useState<CourseContextProps>({
    id,
    courseContent: activeCourseContent,
    preferences: getCoursePreferences(),
    updatePreferences: (prefs) => { },
    gotoError: (err) => { },
    selected,
    setSelected,
    selectedSectionId,
    setSelectedSectionId,
    onContentTypeSelect,
    showAssetsDialog,
    setShowAssetDialog: setShowAssetsDialog,
  });

  const editTransitions = useTransition(!isPublished, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  })

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        preferences: getCoursePreferences(),
        gotoError: handleGoToError,
        setShowValidationErrorDialog: handleShowValidationErrorDialog,
        updatePreferences: handleCoursePreferencesChange
      }
    });
  }, [id]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        courseContent: activeCourseContent
      }
    });
  }, [activeCourseContent]);

  useEffect(() => {
    if (menuState.primaryAction === 'save') {
      handleSave();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        }
      });
    } else if (menuState.primaryAction === 'publish') {
      handlePublish();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        }
      });
    }

  }, [menuState]);

  useEffect(() => {
    if (courseContext.validationError) {
      const type = courseContext.validationError.type;
      const uuid = courseContext.validationError.uuid;

      switch (type) {
        case 'course': {
          navigate(`/coaching/manage/courses/draft/${id}`);
          break;
        }

        case 'assessment': {
          const index = _.findIndex(course.contents, (content) => content.uuid === uuid);
          navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
          break;
        }

        case 'lesson': {
          const index = _.findIndex(course.contents, (content) => content.uuid === uuid);
          navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
          courseContext.gotoError(undefined);
          break;
        }

        default: {
          console.log('Unknown validation error', courseContext.validationError);
        }
      }
    }
  }, [courseContext.validationError]);

  useEffect(() => {
    if (courses.publish?.published) {
      setIsPublished(true);
    }
  }, [courses.publish])

  useEffect(() => {
    setCourseContext(prev => ({
      ...prev,
      showAssetsDialog,
    }));
  }, [showAssetsDialog]);
  
  useEffect(()=> {
    setCourseContext(prev => ({
      ...prev,
      selected
    }))
  },[selected])

  const handleFinishAddLessons = (contents: any) => {
    if (_.size(contents) > 0) {
      contents.filter(item => {
        return (course.contents || []).filter((content) => content.entityType === 'lesson' || content.entityType === "podcast" || content.entityType === "simulation")
          .findIndex((content) => content?.content?.id === item?.id) === -1;
      }).forEach((item: any) => {
        const type = _.get(item,"entityType","lesson")

        const entityType = () =>{
          if(type === "podcast")
            return "podcast"
          else if(type === "simulation")
            return "simulation"
          else 
            return "lesson" 
        }
        
        dispatch(addContentToCourse({
          id: course!.id!,
          content: item,
          order: -1,
          parent: selectedSectionId,
          entityType: entityType()
        }));
      });
    }
  };

  const handleCloseAddLessons = () => {
    setShowAddAssets(false);
  }

  const handleAddAssessmentClicked = (sectionId?: string | null) => {
    setShowTypeSelect(false)
    dispatch(createAssessment({
      course: course.id!,
      name: 'New Assessment',
      order: -1,
      questions: [{
        uuid: nanoid(6),
        name: 'Question 1',
        type: 'multiple_choice',
        answer: 'option-1',
        order: 0,
        options: [{
          uuid: 'option-1',
          name: 'Option 1',
          order: 1,
        }, {
          uuid: 'option-2',
          name: 'Option 2',
          order: 2,
        }, {
          uuid: 'option-3',
          name: 'Option 3',
          order: 3,
        }, {
          uuid: 'option-4',
          name: 'Option 4',
          order: 4,
        }],
      }, {
        uuid: nanoid(6),
        name: 'Question 2',
        type: 'true_false',
        answer: "yes",
        order: 1,
        options: [{
          uuid: 'yes',
          name: 'Yes',
          order: 0,
        }, {
          uuid: 'no',
          name: 'No',
          order: 1,
        }],
      }, {
        uuid: nanoid(6),
        name: 'Question 3',
        type: 'text',
        answer: 'A concise answer to the question that will be evaluated using ai',
        order: 2,
      }],
      parent: sectionId ?? null,
    }));
  }

  const handleAddSectionClicked = (order: number) => {
    dispatch(createCourseSection({
      course: course.id!,
      name: 'Untitled Section',
      order: order,
    }));
  }

  const handleSave = () => {
    const validationErrors = validateCourseRequest(course);

    dispatch(updateValidationErrorsForCourse({
      course: course.id!,
      errors: validationErrors,
    }));

    if (_.size(validationErrors) > 0) {
      courseContext.setShowValidationErrorDialog?.(true);
      return;
    }

    const request = prepareCourseRequest(persistedCourse, course);
    console.log(request);

    dispatch(
      updateCourse({
        id: course.id!,
        request: request,
      }),
    );
  };

  const handlePublish = () => {
    const validationErrors = validateCourseRequest(course);
    dispatch(
      updateValidationErrorsForCourse({
        course: course.id!,
        errors: validationErrors,
      }),
    );

    if (_.size(validationErrors) > 0) {
      courseContext.setShowValidationErrorDialog?.(true);
      return;
    }

    dispatch(publishCourse(course.id!));
  };

  const handleMenuStateChange = (state) => {
    setMenuState(state);
  }

  const handleCoursePreferencesChange = (preferences) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        preferences: preferences
      }
    });

    updateCoursePreferences(preferences);
  }

  const handleGoToError = (error?: CourseValidationError) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        validationError: error
      }
    });
  }

  const handleShowValidationErrorDialog = (show: boolean) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        showValidationErrorDialog: show,
      };
    });
  };

  const handleMoreLearners = () => {
    if (nextPage) {
      const request = {
        course: id,
        status: "ALL",
        page: nextPage,
        limit: 10,
      };
      dispatch(fetchCourseLearners(request));
    }
  }

  const handleStudioClicked =() => {
    setShowTypeSelect(false)
    setShowLearningBrowser(true)
  }

  const handleAddLessonClicked = (sectionId?: string | null) => {
    setSelectedSectionId(sectionId ?? null);
    setShowTypeSelect(true);
  };

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if ((loading && course?.id != id) || !course) {
    return (
      <div className={cx(styles.loadingContainer, styles.content)}>
        <div className={styles.left}>
          <div className={cx(styles.header, styles.placeholder)}>
            <button className={styles.backButton} onClick={() => navigate(`/coaching/manage/courses/draft/${course.id}`)}>
              <i className={'paperflite-left-arrow'}></i>
              <div className={styles.courseName}>course details</div>
            </button>
            <div className={cx(styles.block, styles.title)} style={{ height: "10px" }}></div>
          </div>

          <div className={styles.placeholderCards}>
            {[...Array(3)].map((_, index) => (
              <ContentPlaceholderCard key={index} />
            ))}
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.placeholderContent}>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CourseContext.Provider value={courseContext}>
      {editTransitions((style, item) => {
        return (
          item && (
            <animated.div style={style}>
              <div className={styles.editContainer}>
                <CourseMenuBar
                  course={course}
                  menuState={menuState}
                  onMenuStateChange={handleMenuStateChange}
                />
                <div className={styles.left}>
                  <CourseStructure
                    course={course}
                    contentIndex={contentIndex}
                    contentChildIndex={sectionIndex}
                    onAddLessonClicked={handleAddLessonClicked}
                    onAddAssessmentClicked={handleAddAssessmentClicked}
                    onAddSectionClicked={handleAddSectionClicked}
                  />
                </div>

                <div className={styles.right}>
                  <EditCourseContent
                    course={course}
                    contentIndex={contentIndex}
                    onAddLessonClicked={handleAddLessonClicked}
                    onAddAssessmentClicked={handleAddAssessmentClicked}
                    onAddSectionClicked={handleAddSectionClicked}
                    onContentTypeSelect={onContentTypeSelect}
                  />
                </div>

                  <SelectContentType
                    course={course}
                    openSelectContentType={showTypeSelect}
                    handleClose={()=>setShowTypeSelect(false)}
                    onAddLessonClicked={handleAddLessonClicked}
                    onAddAssessmentClicked={handleAddAssessmentClicked}
                    onStudioClicked={handleStudioClicked}
                    handleFinishAddLessons={handleFinishAddLessons}
                    parentSectionId={selectedSectionId}  
                  />

                <AddAssetsDialog
                  show={showAddAssets}
                  selectableEntityTypes={["asset"]}
                  allowInternalEntities={true}
                  source={{
                    type: "course",
                    entity: course,
                  }}
                  onFinish={handleFinishAddLessons}
                  onClose={handleCloseAddLessons}
                />
              </div>
            </animated.div>
          )
        );
      })}

      <PublishView
        isOpen={isPublished}
        onClose={() => {
          setIsPublished(false);
        }}
        learners={learners || []}
        handleAssignLearners={() => setShowAddLearner(true)}
        loading={loadingLearners}
        hasMoreItems={hasMoreItems}
        handleLoadMoreLearners={handleMoreLearners}
      />

      <AddLearnersDialog
        show={showAddLearner}
        course={course}
        onFinish={() => setShowAddLearner(false)}
        onClose={() => setShowAddLearner(false)}
      />
    </CourseContext.Provider>
  );
}

interface CourseStructureProps {
  course: Course;
  contentIndex: number;
  contentChildIndex: number;
  // eslint-disable-next-line no-unused-vars
  onAddLessonClicked: (parent?: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  onAddAssessmentClicked: (parent?: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  onAddSectionClicked: (order: number) => void;
}

export const CourseStructure: React.FC<CourseStructureProps> = ({ course, contentIndex, contentChildIndex, onAddLessonClicked, onAddAssessmentClicked, onAddSectionClicked }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const courseContext = React.useContext(CourseContext);
  const handleContentTypeSelect = courseContext.onContentTypeSelect;

  const [draggingContent, setDraggingContent] = useState<any>(null);

  const contents = _.get(course, 'contents') || [];

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });

  const sensors = useSensors(
    pointerSensor
  );

  const handleContentClicked = (index: number, section?: number) => {
    console.log(index, section);
    if (course.status !== "NOT_STARTED") {
      if (section != null && section !== -1) {
        navigate(`/coaching/manage/courses/draft/${course.id}/contents?index=${index}&child=${section}`);
      } else {
        navigate(`/coaching/manage/courses/draft/${course.id}/contents?index=${index}`);
      }
    }
  }

  const handleRemove = (parent: string | null, content: string) => {
    dispatch(removeEntityFromCourse({
      course: course.id!,
      parent,
      id: content
    }));
  }

  const handleDragOver = (event) => {
    const { active, over, delta } = event;
    if (!over || (active.parent === over.parent && active.id === over.id)) {
      setDraggingContent({
        active: active,
        over: null
      });

      return;
    };

    const before = delta.y < 0;

    let targetIndex = over.data.current.index;
    if (before) {
      targetIndex = targetIndex - 1;
    }

    console.log(over, over.data.current.index, over.data.current.parent, before);

    if (active.data.current.parent === over.data.current.parent && active.data.current.index === targetIndex) {
      setDraggingContent({
        active: active,
        over: null
      });

      return;
    }

    setDraggingContent({
      active: active,
      over: {
        parent: over.data.current.parent || null,
        index: over.data.current.index,
        before: before
      }
    });
  }

  const handleDragFinish = (event) => {
    const { active, over } = event;
    setDraggingContent(null);

    if (!over || (active.id === over.id)) return;

    // Get source and target entities from draggable items
    const sourceEntity = active.data.current.entity;
    const targetEntity = over.data.current.entity;

    const sourceParentId = active.data.current.parent || null;
    const targetParentId = over.data.current.parent || null;

    if (!sourceEntity?.uuid || !targetEntity?.uuid) {
      return;
    }

    if (sourceEntity.uuid === targetEntity.uuid) {
      return;
    }

    dispatch(rearrangeEntitiesInCourse({
      id: course.id!,
      source: {
        id: sourceEntity.uuid,
        entityType: sourceEntity.entityType,
        parent: sourceParentId,
      },
      target: {
        id: targetEntity.uuid,
        entityType: targetEntity.entityType,
        parent: targetParentId,
      },
    }));
  };

  return (
    <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} autoScroll
      onDragOver={handleDragOver} onDragEnd={handleDragFinish}>
      <div className={styles.content}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(`/coaching/manage/courses/draft/${course.id}`)}>
            <i className={'paperflite-left-arrow'}></i>
            <Text className={styles.courseName} text={course.name} lines={1} tooltip={{ placement: 'bottom' }} />
          </button>

          <div className={styles.details}>
            <h2 className={styles.title}>Course Structure</h2>
            {_.size(contents) > 0 && (
              <div className={styles.addActions}>
                <div className={styles.action} onClick={() => onAddSectionClicked(contents.length)}>
                  <i className="paperflite-stack"></i>
                  <span className={styles.label}>Section</span>
                </div>
                <span className={styles.divider}></span>
                <div className={styles.action} onClick={() => handleContentTypeSelect?.(null)}>
                  <i className="paperflite-multiple-documents"></i>
                  <span className={styles.label}>Units</span>
                </div>
              </div>
            )}
          </div>

        </div>

        <CourseContent
          contents={contents}
          contentIndex={contentIndex}
          contentChildIndex={contentChildIndex}
          dragIndicator={draggingContent?.over}
          onAddSectionClicked={onAddSectionClicked}
          onAddLessonClicked={(parent) => onAddLessonClicked(parent)}
          onAddAssessmentClicked={(parent) => onAddAssessmentClicked(parent)}
          handleContentClicked={handleContentClicked}
          handleRemove={handleRemove}
        />
      </div>

      <DragOverlay>
        {draggingContent?.active && (
          <div style={{ width: '300px' }}>
            {draggingContent.active.data.current.entity.entityType === "lesson" && (
              <LessonCard
                mode="view"
                lesson={draggingContent.active.data.current.entity}
                lessonIndex={getIndexForContent(contents, draggingContent.active.id, draggingContent.active.data.current.parent)}
                index={-1}
                active={false}
                onClick={() => { }}
              />
            )}

            {draggingContent.active.data.current.entity.entityType === "assessment" && (
              <AssessmentCard
                mode="view"
                assessmentIndex={getIndexForContent(contents, draggingContent.active.id, draggingContent.active.data.current.parent)}
                assessment={draggingContent.active.data.current.entity}
                index={-1}
                active={false}
                onClick={() => { }}
              />
            )}
            {draggingContent.active.data.current.entity.entityType === "podcast" && (
              <PodcastCard
                mode="view"
                asset={draggingContent.active.data.current.entity}
                lessonIndex={getIndexForContent(
                  contents,
                  draggingContent.active.id,
                  draggingContent.active.data.current.parent
                )}
                index={-1}
                active={false}
                onClick={() => {}}
              />
            )}
            {draggingContent.active.data.current.entity.entityType === "simulation" && (
              <SimulationCard
                mode="view"
                asset={draggingContent.active.data.current.entity}
                lessonIndex={getIndexForContent(
                  contents,
                  draggingContent.active.id,
                  draggingContent.active.data.current.parent
                )}
                index={-1}
                active={false}
                onClick={() => {}}
              />
            )}

          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

interface CourseContentProps {
  contents: any[];
  contentIndex: number;
  contentChildIndex: number;
  dragIndicator?: {
    index: number;
    parent: string | null;
    before: boolean;
  };
  // eslint-disable-next-line no-unused-vars
  onAddSectionClicked: (order: number) => void;
  // eslint-disable-next-line no-unused-vars
  onAddLessonClicked: (parent?: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  onAddAssessmentClicked: (parent?: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  handleContentClicked: (index: number, section?: number) => void;
  // eslint-disable-next-line no-unused-vars
  handleRemove: (parent: string | null, index: string) => void;
}

const CourseContent: React.FC<CourseContentProps> = ({
  contents,
  contentIndex,
  contentChildIndex,
  dragIndicator,
  onAddSectionClicked,
  onAddLessonClicked,
  onAddAssessmentClicked,
  handleContentClicked,
  handleRemove
}) => {
  const { setNodeRef: setCourseDroppableRef } = useDroppable({
    id: "course",
    data: {
      index: -1,
      entity: { entityType: "course" },
    },
  });

  const sortableItems = useMemo(() => {
    const items: string[] = [];
    contents.forEach((content) => {
      if (content.entityType === "section") {
        items.push(...content.contents.map((c) => c.uuid));
      } else {
        items.push(content.uuid);
      }
    });

    return items;
  }, [contents]);

  if (_.size(contents) === 0) {
    return (
      <div className={styles.placeholderCards}>
        {[...Array(2)].map((_, i) => (
          <ContentEmptyCard key={i} index={i} />
        ))}
      </div>
    );
  }


  return (
    <div className={styles.body} ref={setCourseDroppableRef}>
      <SortableContext
        items={sortableItems}
        strategy={verticalListSortingStrategy}
      >
        {!dragIndicator?.parent && dragIndicator?.index === -1 && dragIndicator?.before && (
          <DropIndicator />
        )}

        {contents.map((content, index) => {
          return (
            <React.Fragment key={content.uuid}>
              {!dragIndicator?.parent && dragIndicator?.index === index && dragIndicator?.before && (
                <DropIndicator />
              )}

              {content.entityType === "lesson" && (
                <LessonCard
                  mode="edit"
                  lesson={content}
                  lessonIndex={getIndexForContent(contents, content.uuid, content.parent)}
                  index={index}
                  active={index === contentIndex}
                  onClick={handleContentClicked}
                  onRemove={handleRemove}
                />
              )}

              {content.entityType === "assessment" && (
                <AssessmentCard
                  mode="edit"
                  assessment={content}
                  assessmentIndex={getIndexForContent(contents, content.uuid, content.parent)}
                  index={index}
                  active={index === contentIndex}
                  onClick={handleContentClicked}
                  onRemove={handleRemove}
                />
              )}

              {content.entityType === "section" && (
                <SectionCard
                  mode="edit"
                  section={content}
                  index={index}
                  sectionIndex={getIndexForContent(contents, content.uuid, content.parent)}
                  contentChildIndex={index === contentIndex ? contentChildIndex : -1}
                  dragIndicator={dragIndicator?.parent === content.uuid ? dragIndicator : undefined}
                  onClick={handleContentClicked}
                  onRemove={handleRemove}
                  onAddLessonClicked={onAddLessonClicked}
                  onAddAssessmentClicked={onAddAssessmentClicked}
                />
              )}

              {
                content.entityType === "podcast" && (
                  <PodcastCard 
                    asset={content}
                    onRemove={handleRemove}
                    mode="edit"
                    index={index}
                    active={index === contentIndex}
                    lessonIndex={getIndexForContent(contents, content.uuid, content.parent)}
                    onClick={handleContentClicked}
                  />
                )
              }

              {
                content.entityType === "simulation" && (
                  <SimulationCard 
                    asset={content}
                    onRemove={handleRemove}
                    mode="edit"
                    index={index}
                    active={index === contentIndex}
                    lessonIndex={getIndexForContent(contents, content.uuid, content.parent)}
                    onClick={handleContentClicked}
                  />
                )
              }


              <div className={styles.dividerLine}>
                <div className={styles.dividerText} onClick={() => onAddSectionClicked(index + 1)}>
                  <i className="paperflite-stack" />
                  <span>Add Section</span>
                </div>
              </div>

              {!dragIndicator?.parent && dragIndicator?.index === index && !dragIndicator?.before && (
                <DropIndicator />
              )}

            </React.Fragment>
          )
        })}

        {!dragIndicator?.parent && dragIndicator?.index === -1 && !dragIndicator?.before && (
          <DropIndicator />
        )}
      </SortableContext>
    </div>
  );
};